// 从 Globalpedia 组件库中复制过来的 FileList 组件，并进行了修改
// File expand on hover

import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useDeepCompareEffect } from 'ahooks';
import { Bubble } from 'tea-component';
import FileItem, { IFileItemProps } from './components/FileItem';
import { getElementStyleMathValue } from '@gp/utils/common';

import type { IBaseProps } from '@home/types/common';
import type { IFile } from '@gp/components/FileList/types';

import './index.scss'; // Combined FileList styles with smart expansion behavior

interface IFileHoverState {
  [key: number]: {
    isHovering: boolean;
    canExpand: boolean;
  };
}

export interface IFileListProps extends IBaseProps {
  files?: IFile[] | null;
  renderActions?: IFileItemProps['renderActions'];
  onFileNameClick?: IFileItemProps['onFileNameClick'];
  onFileNameMouseEnter?: IFileItemProps['onFileNameMouseEnter'];
  onFileNameMouseLeave?: IFileItemProps['onFileNameMouseLeave'];
  onFileItemMouseEnter?: IFileItemProps['onMouseEnter'];
  onFileItemMouseLeave?: IFileItemProps['onMouseLeave'];
  fullWidth?: boolean; // 如果为 true，取消 flex-basis，宽度铺满父组件
  enableSmartExpansion?: boolean; // If false, uses fixed width like globalpedia (default: true) // 如果为 true，文件宽度自适应内容，最大100%，一行放得下多个则在一行，放不下自动换行
  fileItemWidth?: number; // Custom file item width (default: 250)
}

// 单个文件UI的宽度（默认值）
const DEFAULT_FILE_ITEM_WIDTH = 250;
// 单个文件UI的水平间距
const FILE_ITEM_HORIZONTAL_SPACING = 40;
// 单个文件UI的纵向间距
const FILE_ITEM_VERTICAL_SPACING = 16;

const calcFilesLengthPerRow = (container?: HTMLDivElement | null, fileItemWidth: number = DEFAULT_FILE_ITEM_WIDTH): number => {
  if (!container) return 0;
  const containerWidth = getElementStyleMathValue(container, 'width');
  return Math.floor((containerWidth + FILE_ITEM_HORIZONTAL_SPACING) / (fileItemWidth + FILE_ITEM_HORIZONTAL_SPACING));
};

const useLengthPerRow = (container?: React.RefObject<HTMLDivElement | null>, files?: IFile[] | null, fileItemWidth: number = DEFAULT_FILE_ITEM_WIDTH) => {
  const [length, setLength] = useState(0);

  const updateLength = () => {
    if (!container?.current) return;
    setLength(calcFilesLengthPerRow(container.current, fileItemWidth));
  };

  useDeepCompareEffect(() => {
    updateLength();
  }, [files]);

  useEffect(() => {
    window.addEventListener('resize', updateLength, false);

    return () => {
      window.removeEventListener('resize', updateLength, false);
    };
  }, []);

  return length;
};

const FileList: React.FC<IFileListProps> = (props) => {
  const {
    style,
    className,
    files,
    renderActions,
    onFileNameClick,
    onFileNameMouseEnter,
    onFileNameMouseLeave,
    onFileItemMouseEnter,
    onFileItemMouseLeave,
    fullWidth = false,
    enableSmartExpansion = false, // Default: use strategic's flexible behavior
    fileItemWidth = DEFAULT_FILE_ITEM_WIDTH, // Default: 250px
  } = props;

  const container = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<{ [key: number]: HTMLLIElement | null }>({});
  const [hoverStates, setHoverStates] = useState<IFileHoverState>({});

  const isAutoWrapMode = enableSmartExpansion && !fullWidth;

  const lengthPerRow = useLengthPerRow(container, files, fileItemWidth);

  const filesLength = files?.length || 0;
  const rows = Math.ceil(filesLength / lengthPerRow);
  // 最后一行的开始索引
  const specialIndex = (rows - 1) * lengthPerRow;

  // Check if expanding an item would exceed parent container
  const checkCanExpand = (itemIndex: number): boolean => {
    // If smart expansion disabled, always return false (fixed width like globalpedia)
    if (!enableSmartExpansion) return false;
    
    const containerEl = container.current;
    const itemEl = itemRefs.current[itemIndex];
    
    // Full-width layout can always expand
    if (fullWidth) return true;
    
    if (!containerEl || !itemEl) return false;
    
    const containerWidth = containerEl.offsetWidth;
    const itemRect = itemEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    
    // Calculate the item's position relative to container
    const itemLeftInContainer = itemRect.left - containerRect.left;
    const currentItemWidth = itemRect.width;
    
    // Estimate expanded width (current width + action buttons width)
    // Action icons take approximately 110-120px (3 icons + spacing)
    const ACTION_ICONS_WIDTH = 120;
    const estimatedExpandedWidth = currentItemWidth + ACTION_ICONS_WIDTH;
    
    // Check if expanded width would exceed container (with small buffer)
    const BUFFER = 10; // 10px safety margin
    const wouldExceed = itemLeftInContainer + estimatedExpandedWidth + BUFFER > containerWidth;
    
    return !wouldExceed;
  };

  const handleItemMouseEnter = (file: IFile | null, fileIdx: number) => {
    // If smart expansion disabled, no need to check (fixed width mode)
    if (!enableSmartExpansion) {
      onFileItemMouseEnter?.(file);
      return;
    }
    
    // Use setTimeout to ensure the DOM has updated before checking
    setTimeout(() => {
      const canExpand = checkCanExpand(fileIdx);
      setHoverStates(prev => ({
        ...prev,
        [fileIdx]: { isHovering: true, canExpand }
      }));
    }, 0);
    onFileItemMouseEnter?.(file);
  };

  const handleItemMouseLeave = (file: IFile | null, fileIdx: number) => {
    setHoverStates(prev => ({
      ...prev,
      [fileIdx]: { isHovering: false, canExpand: false }
    }));
    onFileItemMouseLeave?.(file);
  };

  return (
    <div ref={container} style={style} className={classnames('dbs-common-file-list', className)}>
      <ul className="dbs-common-file-list__list">
        {files?.map((file, fileIdx) => {
          if (!file) return null;
          const hoverState = hoverStates[fileIdx];
          const isHovering = hoverState?.isHovering ?? false;
          const canExpand = hoverState?.canExpand ?? false;
          
          return (
            <li
              key={fileIdx}
              ref={(el) => { itemRefs.current[fileIdx] = el; }}
              style={{
                /**
                 * 1、最后一个marginRight为0
                 * 2、每一行最后一个marginRight为0
                 * 3、其余情况marginRight为FILE_ITEM_HORIZONTAL_SPACING
                 */
                marginRight:
                  isAutoWrapMode
                    ? FILE_ITEM_HORIZONTAL_SPACING // 智能扩展自适应模式：所有项都有右边距，让flex-wrap自动换行
                    : (fileIdx === filesLength - 1 || (fileIdx + 1) % lengthPerRow === 0 ? 0 : FILE_ITEM_HORIZONTAL_SPACING),
                /**
                 * 1、最后一行marginBottom为0
                 * 3、其余marginBottom为FILE_ITEM_VERTICAL_SPACING
                 */
                marginBottom: isAutoWrapMode ? FILE_ITEM_VERTICAL_SPACING : (fileIdx >= specialIndex ? 0 : FILE_ITEM_VERTICAL_SPACING),
                // 智能扩展自适应模式：宽度自适应内容，最大100%，自动换行
                // fullWidth模式：宽度铺满父组件
                // 默认模式：固定230px
                ...(fullWidth
                  ? { width: '100%' }
                  : isAutoWrapMode
                    ? {
                        flex: '0 1 auto', // 允许收缩和扩展，但不占据剩余空间
                        maxWidth: '100%', // 最大宽度100%
                        width: 'auto', // 宽度自适应内容
                      }
                    : { flexBasis: fileItemWidth }
                ),
              }}
              className={classnames('dbs-common-file-list__item', {
                // Apply expansion classes only if smart expansion is enabled
                'dbs-common-file-list__item--can-expand': enableSmartExpansion && isHovering && canExpand,
                'dbs-common-file-list__item--cannot-expand': enableSmartExpansion && isHovering && !canExpand,
                // Use globalpedia fixed style if smart expansion is disabled
                'dbs-common-file-list__item--fixed': !enableSmartExpansion,
              })}
            >
              <FileItem
                file={file}
                renderActions={renderActions}
                onFileNameClick={onFileNameClick}
                onFileNameMouseEnter={onFileNameMouseEnter}
                onFileNameMouseLeave={onFileNameMouseLeave}
                onMouseEnter={() => handleItemMouseEnter(file, fileIdx)}
                onMouseLeave={() => handleItemMouseLeave(file, fileIdx)}
                renderFileName={(fileNameElement) => (
                  <Bubble
                    dark
                    content={file.name ? `${file.name}${file.format ? `.${file.format}` : ''}` : ''}
                    trigger="hover"
                  >
                    {fileNameElement}
                  </Bubble>
                )}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FileList;
